import React from 'react';
import { Button, Text } from '@chakra-ui/react';

import {
  Modal,
  InputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  CurrencyInputWithConfirm,
  AutocompleteInputWithConfirm,
} from 'shared-components';

import { StudentState } from '../../../../store/student/types';
import { countries } from 'platform-data';
import { FamilySponsorship, FundingSummary } from '../../../../types';
import FundingCoverView from '../../../atoms/funding-cover-view';
import { formatCurrency } from '../../../../utilities/formatting';
import {
  getPageStatus,
  isRestrictedCountry,
  getCountryBanks,
  getAccountDesc,
  getCoverStatus,
  getAccountNameString,
  familySponsorTypeLabel,
  fromFamilySponsorTypeLabel,
} from './utils';

import { FamilySponsorType } from 'api-paths-cas';

interface IProps {
  sponsorKey: string | null;
  id: string;
  fundingSummary: FundingSummary;
  contributionAmount: number;
  saveButtonText: string;
  config: StudentState['config'];
  hasPartner: boolean;
  onSave: (data: FamilySponsorship) => void;
  onClose: () => void;
}

type IComponentProps = Partial<FamilySponsorship> & IProps;

interface IState {
  sponsorType: string;
  sponsorName: string;
  sponsorNameOther: string;
  accountDesc: string;
  jointType: string;
  accountFirstName: string;
  accountSurname: string;
  country: string;
  countrySuggestions: string[];
  bankNameSuggestions: string[];
  bankName: string;
  isOtherBankName: boolean;
  amountCovered: number;
  totalAmount: number;
  canWithdrawImmediately: boolean | undefined;
  accountFirstNameError: string;
  accountSurnameError: string;
  sponsorNameOtherError: string;
  countryError: string;
  bankNameError: string;
  amountCoveredError: string;
  totalAmountError: string;
  amountCoveredWarning: string;
}

class BankSponsor extends React.Component<IComponentProps, IState> {
  countries: Array<string>;

  constructor(props: IComponentProps) {
    super(props);
    this.countries = [...new Set(countries.map(country => country.countryName))];
    const bankName = props.bankName || '';
    const country = props.country || '';
    const isOtherBankName =
      isRestrictedCountry(country) && bankName !== ''
        ? !getCountryBanks(country).some(bank => bank === bankName)
        : false;
    this.state = {
      sponsorType: props.sponsorType || '',
      sponsorName: props.accountType || '',
      sponsorNameOther: props.otherReason || '',
      accountDesc: getAccountDesc(props.accountType || ''),
      jointType: props.jointType || '',
      accountFirstName: props.accountFirstName || '',
      accountSurname: props.accountSurname || '',
      country,
      countrySuggestions: [],
      bankNameSuggestions: [],
      bankName,
      isOtherBankName,
      amountCovered: props.sponsorshipAmount || 0,
      totalAmount: props.totalAmount || 0,
      canWithdrawImmediately: props.canWithdrawImmediately ?? undefined,

      sponsorNameOtherError: '',
      accountFirstNameError: '',
      accountSurnameError: '',
      countryError: '',
      bankNameError: '',
      amountCoveredError: '',
      totalAmountError: '',
      amountCoveredWarning: '',
    };
  }

  componentDidUpdate(_prevProps: IComponentProps, prevState: IState) {
    if (prevState.accountDesc !== this.state.accountDesc) {
      if (this.state.accountDesc === 'My own account') {
        this.setState({
          sponsorName: 'Mine',
        });
      } else if (this.state.accountDesc === 'My joint account with someone else') {
        this.setState({
          sponsorName: 'Joint',
        });
      } else if (this.state.accountDesc === "Someone else's account") {
        this.setState({
          sponsorName: '',
        });
      }
    }

    if (prevState.sponsorName !== this.state.sponsorName) {
      this.setState({
        accountFirstName: '',
        accountSurname: '',
      });

      if (this.state.sponsorName !== 'Joint') {
        this.setState({
          jointType: '',
        });
      }
      if (this.state.sponsorName !== 'Other') {
        this.setState({
          sponsorNameOther: '',
        });
      }
    }

    if (prevState.jointType !== this.state.jointType) {
      this.setState({
        accountFirstName: '',
        accountSurname: '',
      });

      if (this.state.jointType !== 'Other') {
        this.setState({
          sponsorNameOther: '',
        });
      }
    }

    if (prevState.country !== this.state.country || prevState.isOtherBankName !== this.state.isOtherBankName) {
      this.setState({
        bankName: '',
      });
    }
  }

  onFamilySponsorTypeClick = (question: IQuestion) => {
    this.setState({
      sponsorType: fromFamilySponsorTypeLabel(question.label),
    });
  };

  onAccountTypeClick = (question: IQuestion) => {
    let value = '';

    switch (question.label) {
      case 'Mother':
        value = 'Mother';
        break;
      case 'Father':
        value = 'Father';
        break;
      case 'Guardian':
        value = 'Guardian';
        break;
      case 'Partner':
        value = 'Spouse';
        break;
      case 'Other':
        value = 'Other';
        break;
      default:
        value = '';
        break;
    }

    this.setState({
      sponsorName: value,
    });
  };

  onAccountTypeKeyDown = (event: any, question: IQuestion) => {
    if (event.key === ' ' || event.key === 'Enter') {
      this.onAccountTypeClick(question);
    }
  };

  onJointTypeClick = (question: IQuestion) => {
    let value = '';

    switch (question.label) {
      case 'Mother':
        value = 'Mother';
        break;
      case 'Father':
        value = 'Father';
        break;
      case 'Guardian':
        value = 'Guardian';
        break;
      case 'Partner':
        value = 'Spouse';
        break;
      case 'Other':
        value = 'Other';
        break;
      default:
        value = '';
        break;
    }

    this.setState({
      jointType: value,
    });
  };

  onBlurCountry = () => {
    const value = this.state.country.trim();
    const countryError = this.countries.includes(value) ? '' : 'Please enter a country';
    const country = this.countries.includes(value) ? value : '';

    this.setState({
      country,
      countrySuggestions: [],
      countryError,
    });
  };

  onFocusCountry = () => {
    this.setState({
      countryError: '',
    });
  };

  onBlurManualEntryBankName = () => {
    const value = this.state.bankName.trim();
    const bankNameError = value === '' ? 'Please enter a bank name' : '';
    this.setState({
      bankName: value,
      bankNameError,
    });
  };

  onBlurBankName = () => {
    const value = this.state.bankName.trim();
    const bankName = getCountryBanks(this.state.country).find(bank => bank === value) || '';
    const bankNameError = bankName === '' ? 'Please enter a bank name' : '';

    this.setState({
      bankName,
      bankNameError,
      bankNameSuggestions: [],
    });
  };

  onFocusBankName = () => {
    this.setState({
      bankNameError: '',
    });
  };

  onBlurAmountCovered = () => {
    const amountCoveredError =
      this.state.amountCovered !== 0 ? this.getAmountCoveredError(this.state.amountCovered) : 'Please enter an amount';
    const amountCoveredWarning = this.getAmountCoveredWarning(this.state.amountCovered);
    this.setState({
      amountCoveredError,
      amountCoveredWarning,
      totalAmountError:
        this.state.totalAmount < this.state.amountCovered
          ? "Total amount in the bank account can't be lower than the amount of sponsorship"
          : '',
    });
  };

  onFocusAmountCovered = () => {
    this.setState({
      amountCoveredError: '',
      amountCoveredWarning: '',
    });
  };

  onBlurTotalAmount = () => {
    this.setState({
      totalAmountError:
        this.state.totalAmount === 0
          ? 'Please enter an amount'
          : this.state.totalAmount < this.state.amountCovered
            ? "Total amount in the bank account can't be lower than the amount of sponsorship"
            : '',
    });
  };

  onFocusTotalAmount = () => {
    this.setState({
      totalAmountError: '',
    });
  };

  onBankNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '';
    const suggestions = getCountryBanks(this.state.country).filter(bank =>
      bank.toLowerCase().includes(value.toLowerCase()),
    );

    this.setState({
      bankNameSuggestions: suggestions,
      bankName: value,
    });
  };

  onToggleManualBankNameEntry = () => {
    this.setState({
      isOtherBankName: !this.state.isOtherBankName,
    });
  };

  onManualBankNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '';
    this.setState({
      bankName: value,
    });
  };

  onCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = this.countries.filter(country => country.toLowerCase().includes(value.toLowerCase()));
    this.setState({
      countrySuggestions: suggestions,
      country: value,
    });
  };

  onAmountCoveredChange = (event: React.FormEvent<HTMLInputElement>) => {
    const amountCovered = Number(event.currentTarget.value) || 0;
    this.setState({
      amountCovered,
    });
  };

  onTotalAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
    const totalAmount = Number(event.currentTarget.value) || 0;
    this.setState({
      totalAmount,
    });
  };

  getAmountLeftToProve = (sponsorshipAmount: number) => {
    const { fundingSummary, contributionAmount, sponsorKey } = this.props;
    let remainingAmountToProve = fundingSummary.totalRemaining;

    if (sponsorKey !== null) {
      if (fundingSummary.totalRemaining === 0) {
        remainingAmountToProve = contributionAmount - sponsorshipAmount;
      } else {
        remainingAmountToProve = fundingSummary.totalRemaining + contributionAmount - sponsorshipAmount;
      }
    } else {
      remainingAmountToProve -= sponsorshipAmount;
    }
    return remainingAmountToProve > 0 ? remainingAmountToProve : 0;
  };

  areFundsOverThreshold = (value: number) => {
    const { fundingSummary, contributionAmount, sponsorKey } = this.props;
    let amountLeftToProve = fundingSummary.totalRemaining;
    if (sponsorKey !== null) {
      amountLeftToProve += contributionAmount;
    }
    const maxAmountCovered = (150 / 100) * amountLeftToProve;
    return value ? value > maxAmountCovered : false;
  };

  areFundsBetweenThreshold = (value: number) => {
    const { fundingSummary, contributionAmount, sponsorKey } = this.props;
    let amountLeftToProve = fundingSummary.totalRemaining;
    if (sponsorKey !== null) {
      amountLeftToProve += contributionAmount;
    }
    const maxAmountCovered = (125 / 100) * amountLeftToProve;
    const minAmountCovered = amountLeftToProve;
    return value ? value <= maxAmountCovered && value >= minAmountCovered : false;
  };

  getRemainingAmountToProve = (thresholdPerc?: number) => {
    const { fundingSummary, contributionAmount, sponsorKey } = this.props;
    let amountLeftToProve = fundingSummary.totalRemaining;
    if (sponsorKey !== null) {
      amountLeftToProve += contributionAmount;
    }
    const amount = Math.round((thresholdPerc! / 100) * amountLeftToProve);
    if (amount > 0) {
      return formatCurrency(amount);
    }
    return 0;
  };

  getAmountCoveredError = (value: number = this.state.amountCovered) => {
    if (this.areFundsOverThreshold(value)) {
      return `Please be aware this amount is significantly higher than the amount you need to prove. Please enter a value less than 150% of the required amount (£${this.getRemainingAmountToProve(
        150,
      )}).`;
    }
    return '';
  };

  getAmountCoveredWarning = (value: number = this.state.amountCovered!) => {
    if (this.areFundsBetweenThreshold(value)) {
      return `Be aware amounts under 125% of the required amount (£${this.getRemainingAmountToProve(
        125,
      )}) could be vulnerable to currency fluctuations`;
    }
    return '';
  };

  getPageStatus = () => {
    return getPageStatus({
      sponsorType: this.state.sponsorType,
      accountDesc: this.state.accountDesc,
      accountType: this.state.sponsorName,
      jointType: this.state.jointType,
      accountFirstName: this.state.accountFirstName,
      accountSurname: this.state.accountSurname,
      otherReason: this.state.sponsorNameOther,
      country: this.state.country,
      bankName: this.state.bankName,
      totalAmount: this.state.totalAmount,
      sponsorshipAmount: this.state.amountCovered,
      fundsOverThreshold: this.areFundsOverThreshold(this.state.amountCovered),
      isOtherBankName: this.state.isOtherBankName,
      canWithdrawImmediately: this.state.canWithdrawImmediately,
    });
  };

  onSaveRegionDetails = () => {
    this.props.onSave({
      sponsorType: this.state.sponsorType as FamilySponsorship['sponsorType'],
      accountType: this.state.sponsorName as FamilySponsorship['accountType'],
      jointType: this.state.jointType as FamilySponsorship['jointType'],
      accountFirstName: this.state.accountFirstName,
      accountSurname: this.state.accountSurname,
      otherReason: this.state.sponsorNameOther,
      country: this.state.country,
      bankName: this.state.bankName,
      totalAmount: this.state.totalAmount,
      sponsorshipAmount: this.state.amountCovered,
      canWithdrawImmediately: this.state.canWithdrawImmediately!,
    });

    this.props.onClose();
  };

  onWithdrawClick = (question: IQuestion) => {
    this.setState({
      canWithdrawImmediately: question.label === 'Yes',
    });
  };

  render() {
    const { complete, visible } = this.getPageStatus();

    return (
      <Modal
        id={this.props.id}
        title="Provide details for bank account"
        show={true}
        onClose={this.props.onClose}
        footer={
          <Button
            id="saveButton"
            onClick={this.onSaveRegionDetails}
            isDisabled={!complete}
            size={'lg'}
            variant={'solid'}
          >
            {this.props.saveButtonText}
          </Button>
        }
      >
        {visible.sponsorType && (
          <RadioOptionsWithConfirm
            id={'sponsor_type'}
            label="What is the account type?"
            questions={[
              {
                char: '1',
                label: familySponsorTypeLabel(FamilySponsorType.STANDARD),
                selected: this.state.sponsorType === FamilySponsorType.STANDARD,
              },
              {
                char: '2',
                label: familySponsorTypeLabel(FamilySponsorType.TERM_DEPOSIT),
                selected: this.state.sponsorType === FamilySponsorType.TERM_DEPOSIT,
              },
              {
                char: '3',
                label: familySponsorTypeLabel(FamilySponsorType.SAVINGS),
                selected: this.state.sponsorType === FamilySponsorType.SAVINGS,
              },
              {
                char: '4',
                label: familySponsorTypeLabel(FamilySponsorType.INVESTMENT),
                selected: this.state.sponsorType === FamilySponsorType.INVESTMENT,
              },
              {
                char: '5',
                label: familySponsorTypeLabel(FamilySponsorType.PENSION),
                selected: this.state.sponsorType === FamilySponsorType.PENSION,
              },
            ]}
            onClick={this.onFamilySponsorTypeClick}
          />
        )}

        {visible.accountDesc && (
          <RadioOptionsWithConfirm
            id="accountDesc"
            label="What best describes the account?"
            questions={[
              {
                char: '1',
                label: 'My own account',
                selected: this.state.accountDesc === 'My own account',
              },
              {
                char: '2',
                label: "Someone else's account",
                selected: this.state.accountDesc === "Someone else's account",
              },
              {
                char: '3',
                label: 'My joint account with someone else',
                selected: this.state.accountDesc === 'My joint account with someone else',
              },
            ]}
            onClick={(question: IQuestion) => {
              this.setState({
                accountDesc: question.label,
              });
            }}
          />
        )}

        {visible.jointType && (
          <RadioOptionsWithConfirm
            id="jointType"
            label="Who is the other person named on the joint account?"
            questions={[
              {
                char: '1',
                label: 'Mother',
                selected: this.state.jointType === 'Mother',
              },
              {
                char: '2',
                label: 'Father',
                selected: this.state.jointType === 'Father',
              },
              {
                char: '3',
                label: 'Guardian',
                selected: this.state.jointType === 'Guardian',
              },
              {
                char: '4',
                label: 'Partner',
                selected: this.state.jointType === 'Spouse',
              },
              {
                char: '5',
                label: 'Other',
                selected: this.state.jointType === 'Other',
              },
            ]}
            onClick={this.onJointTypeClick}
          />
        )}

        {visible.accountOwner && (
          <RadioOptionsWithConfirm
            id="sponsorName"
            label="Who is the owner of the account?"
            questions={[
              {
                char: '1',
                label: 'Mother',
                selected: this.state.sponsorName === 'Mother',
              },
              {
                char: '2',
                label: 'Father',
                selected: this.state.sponsorName === 'Father',
              },
              {
                char: '3',
                label: 'Guardian',
                selected: this.state.sponsorName === 'Guardian',
              },
              {
                char: '4',
                label: 'Partner',
                selected: this.state.sponsorName === 'Spouse',
              },
              {
                char: '5',
                label: 'Other',
                selected: this.state.sponsorName === 'Other',
              },
            ]}
            onClick={this.onAccountTypeClick}
            inputHelperText={
              'Guardian refers to a legal guardian; Partner refers to the applicant’s spouse, civil partner, fiancé(e), proposed civil partner, or unmarried partner.'
            }
          />
        )}

        {visible.accountFirstName && (
          <InputWithConfirm
            id="accountFirstName"
            value={this.state.accountFirstName}
            label={`What is your ${getAccountNameString(this.state.sponsorName, this.state.jointType)}'s first name?`}
            errorText={this.state.accountFirstNameError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                accountFirstName: event.currentTarget.value,
              });
            }}
            onBlur={(_event: React.FormEvent<HTMLInputElement>) => {
              if (this.state.accountFirstName === '') {
                this.setState({
                  accountFirstNameError: 'Please fill this in',
                });
              }
            }}
            onFocus={(_event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                accountFirstNameError: '',
              });
            }}
          />
        )}

        {visible.accountSurname && (
          <InputWithConfirm
            id="accountSurname"
            value={this.state.accountSurname}
            label={`What is your ${getAccountNameString(this.state.sponsorName, this.state.jointType)}'s family name?`}
            errorText={this.state.accountSurnameError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                accountSurname: event.currentTarget.value,
              });
            }}
            onBlur={(_event: React.FormEvent<HTMLInputElement>) => {
              if (this.state.accountSurname === '') {
                this.setState({
                  accountSurnameError: 'Please fill this in',
                });
              }
            }}
            onFocus={(_event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                accountSurnameError: '',
              });
            }}
          />
        )}

        {visible.otherReason && (
          <InputWithConfirm
            id="account-name-other"
            value={this.state.sponsorNameOther}
            label="Please explain why you've chosen 'other'?"
            errorText={this.state.sponsorNameOtherError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                sponsorNameOther: event.currentTarget.value,
              });
            }}
            onBlur={(_event: React.FormEvent<HTMLInputElement>) => {
              if (this.state.sponsorNameOther === '') {
                this.setState({
                  sponsorNameOtherError: 'Please fill this in',
                });
              }
            }}
            onFocus={(_event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                sponsorNameOtherError: '',
              });
            }}
          />
        )}

        {visible.country && (
          <AutocompleteInputWithConfirm
            id="country"
            label="Country bank account is located"
            onChange={this.onCountryChange}
            value={this.state.country}
            showErrorText={!!this.state.countryError}
            errorText={this.state.countryError}
            suggestions={this.state.countrySuggestions}
            onBlur={this.onBlurCountry}
            onFocus={this.onFocusCountry}
          />
        )}

        {visible.bankName && (
          <>
            <AutocompleteInputWithConfirm
              id="restrictedBanks"
              label="Please select the bank"
              onChange={this.onBankNameChange}
              value={this.state.bankName}
              showErrorText={!!this.state.bankNameError}
              errorText={this.state.bankNameError}
              suggestions={this.state.bankNameSuggestions}
              onBlur={this.onBlurBankName}
              onFocus={this.onFocusBankName}
              mb={3}
            />

            <Button onClick={this.onToggleManualBankNameEntry} mb={10}>
              Can't find your bank on the list?
            </Button>
          </>
        )}

        {visible.bankOtherName && (
          <>
            <InputWithConfirm
              id="restrictedBanks"
              label="Enter bank name not on list"
              value={this.state.bankName}
              errorText={this.state.bankNameError}
              onChange={this.onManualBankNameChange}
              onBlur={this.onBlurManualEntryBankName}
              onFocus={this.onFocusBankName}
              mb={3}
            />

            <Button onClick={this.onToggleManualBankNameEntry} mb={10}>
              Back to list
            </Button>
          </>
        )}

        {visible.totalAmount && (
          <CurrencyInputWithConfirm
            id="bank_amount"
            value={this.state.totalAmount}
            errorText={this.state.totalAmountError}
            onChange={this.onTotalAmountChange}
            onFocus={this.onFocusTotalAmount}
            onBlur={this.onBlurTotalAmount}
            inputHelperText={this.props.config.Checklist.Finances.Page1.TotalAmountInAccount?.helpText}
            label="What is the current balance of the account in pounds?"
          />
        )}

        {visible.sponsorshipAmount && (
          <CurrencyInputWithConfirm
            id="total_amount"
            currencySymbol="£"
            value={this.state.amountCovered}
            warningText={this.state.amountCoveredWarning}
            errorText={this.state.amountCoveredError}
            onChange={this.onAmountCoveredChange}
            onFocus={this.onFocusAmountCovered}
            onBlur={this.onBlurAmountCovered}
            label="How much of this money is being allocated to your overseas study?"
            subText={`Remaining amount to prove: £${formatCurrency(
              this.getAmountLeftToProve(this.state.amountCovered),
            )}`}
          />
        )}

        {visible.canWithdrawImmediately && (
          <RadioOptionsWithConfirm
            id="canWithdrawImmediately"
            label={this.props.config.Checklist.Finances.Page1.CanWithdrawImmediately.label}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: this.state.canWithdrawImmediately === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: this.state.canWithdrawImmediately === false,
              },
            ]}
            isInline={true}
            inputHelperText={this.props.config.Checklist.Finances.Page1.CanWithdrawImmediately.helpText}
            onClick={this.onWithdrawClick}
          />
        )}

        <Text fontSize={'lg'}>The money needs to be in the account for 28 consecutive days</Text>

        {visible.fundingCover && (
          <FundingCoverView
            label={
              this.state.canWithdrawImmediately === false
                ? 'Funds that cannot be accessed immediately, regardless of notice period, cannot not be accepted as evidence of funds for your Student visa application'
                : this.state.sponsorName === 'Other'
                  ? 'As you selected "other" we are unable to determine if this account can be used towards your costs'
                  : 'This funding source is eligible to be used towards the following items'
            }
            fundingSummary={this.props.fundingSummary}
            coverStatus={getCoverStatus(
              this.state.sponsorName,
              this.state.jointType,
              this.state.canWithdrawImmediately,
              this.props.hasPartner,
            )}
          />
        )}
      </Modal>
    );
  }
}

export default BankSponsor;
